import { useMemo, useState } from "react";
import { works } from "@/mocks/works";
import WorkCard from "@/components/feature/WorkCard";
import PageHeader from "@/components/feature/PageHeader";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useSEO } from "@/hooks/useSEO";

const typeOptions = ["全部", "电影", "电视剧"];
const yearOptions = ["全部", "2025", "2024", "2023", "2022", "2021"];
const statusOptions = ["全部", "热播中", "即将上映", "已上映", "已完结"];
const genreOptions = [
  "全部",
  "悬疑",
  "剧情",
  "奇幻",
  "古装",
  "犯罪",
  "科幻",
  "家庭",
  "历史",
  "爱情",
  "青春",
  "谍战",
  "动作",
  "文艺",
];
const sortOptions = [
  { key: "latest", label: "最新上线" },
  { key: "rating", label: "评分最高" },
  { key: "heat", label: "热度最高" },
] as const;

const PAGE_SIZE = 8;

function FilterRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-3 first:pt-0 last:border-b-0 last:pb-0 sm:flex-row sm:items-center">
      <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
              value === opt
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Works() {
  useSEO({
    title: "影视作品 - 朝桐光官方展示",
    description:
      "浏览朝桐光全部影视作品，涵盖电影、电视剧及悬疑、科幻、历史、家庭等多元题材，支持按类型、年代、状态筛选与排序。",
    keywords: "朝桐光影视作品,朝桐光电影,朝桐光电视剧,影视作品列表",
  });

  const [type, setType] = useState("全部");
  const [year, setYear] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState<(typeof sortOptions)[number]["key"]>("latest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = works.filter((w) => {
      if (type !== "全部" && w.type !== type) return false;
      if (year !== "全部" && String(w.year) !== year) return false;
      if (status !== "全部" && w.status !== status) return false;
      if (genre !== "全部" && !w.genre.includes(genre)) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === "heat") sorted.sort((a, b) => b.heat - a.heat);
    else sorted.sort((a, b) => b.year - a.year);
    return sorted;
  }, [type, year, status, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetAll = () => {
    setType("全部");
    setYear("全部");
    setStatus("全部");
    setGenre("全部");
    setSort("latest");
    setPage(1);
  };

  const change = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="影视作品"
        desc="朝桐光出品的电影与电视剧作品全集，支持按类型、年代、状态与题材筛选，并可切换最新、评分与热度排序。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "影视作品" }]}
        aside={
          <div className="flex items-center gap-6 rounded-lg border border-primary-900 bg-primary-900/40 px-5 py-3">
            <div>
              <div className="font-heading text-2xl font-black text-accent-400">{works.length}</div>
              <div className="text-xs text-background-300">全部作品</div>
            </div>
            <div className="h-8 w-px bg-primary-800" />
            <div>
              <div className="font-heading text-2xl font-black text-background-50">{filtered.length}</div>
              <div className="text-xs text-background-300">筛选结果</div>
            </div>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
          <FilterRow label="类型" options={typeOptions} value={type} onChange={change(setType)} />
          <FilterRow label="年代" options={yearOptions} value={year} onChange={change(setYear)} />
          <FilterRow label="状态" options={statusOptions} value={status} onChange={change(setStatus)} />
          <FilterRow label="题材" options={genreOptions} value={genre} onChange={change(setGenre)} />
          <div className="flex flex-col gap-2 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">排序</span>
              <div className="flex flex-wrap gap-1.5">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setSort(opt.key);
                      setPage(1);
                    }}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                      sort === opt.key
                        ? "bg-accent-500 text-foreground-950"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600 sm:self-auto"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>

        <div className="mt-8">
          {pageItems.length === 0 ? (
            <EmptyState
              title="没有找到符合条件的作品"
              desc="试试调整筛选条件，或点击下方按钮重置全部筛选。"
              onReset={resetAll}
            />
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          )}
        </div>

        {pageItems.length > 0 && (
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </div>
  );
}